import React, { useState, useEffect } from 'react';
import UserHistory from "./UserHistory";
import {
  FlameIcon, ArrowLeft,
  GaugeIcon,
  CloudIcon,
  DropletIcon,
  WindIcon,
  OctagonIcon, LogOut, Search, LayoutDashboard,
  FileText, ServerIcon, WifiIcon, PowerIcon, Power
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Chart from 'react-apexcharts'; // Default import for react-apexcharts v1.8.0
import UserHeader from './UserHeader';

const UserDashboard = ({ onLogout }) => {
  const deviceDetails = {
    serialNumber: 'HW-1',
    firmwareVersion: 'v2.3.1',
    installationDate: "12-12-2022",
    batteryLevel: '85%',
    networkConnection: "online",
    additionalInfo: {
      manufacturer: 'TechCorp',
      modelNumber: 'ABC-123',
      warranty: 'Active until 2026'
    }
  };
 
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    window.location.href = "/"; // Redirect to login page
  };
  const [isPowered, setIsPowered] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [initialLoad, setInitialLoad] = useState(true);
  // Fixed constant data for powered off state
  const fixedData = {
    timestamp: new Date().toLocaleString(),
    param1: 65.00, // Methane Upgradation % v/v
    param2: 0.50, // O2 Up % v/v
    param3: 5.00, // H2S Up ppm
    param4: 150.00, // CO2 Up ppm
    param9: 60.00, // Methane Digestor % v/v
    param10: 1.00, // O2 Dig % v/v
    param11: 15.00, // H2S Dig ppm
    param12: 200.00, // CO2 Dig ppm
    param13: 45.00, // Upgradation Raw Bio-Gas m³/h
    param14: 50.00, // Digestor Raw Bio-Gas m³/h
    param15: 100.00 // Feedstock m³/h
  };
  const now = Date.now();
  const phase = now / 100000;
  // Compute initial values
  const initialParam1 = parseFloat((35 + Math.sin(phase) * 35).toFixed(2));
  const initialParam2 = parseFloat((0.5 + Math.sin(phase + 1) * 0.5).toFixed(2));
  const initialParam3 = parseFloat((10 + Math.sin(phase + 2) * 10).toFixed(2));
  const initialParam4 = parseFloat((250 + Math.sin(phase + 3) * 150).toFixed(2));
  const initialParam9 = parseFloat((32.5 + Math.sin(phase + 4) * 32.5).toFixed(2));
  const initialParam10 = parseFloat((0.5 + Math.sin(phase + 5) * 0.5).toFixed(2));
  const initialParam11 = parseFloat((9 + Math.sin(phase + 6) * 9).toFixed(2));
  const initialParam12 = parseFloat((300 + Math.sin(phase + 7) * 150).toFixed(2));
  const initialParam13 = parseFloat((25 + Math.sin(phase + 8) * 25).toFixed(2));
  const initialParam14 = parseFloat((22.5 + Math.sin(phase + 9) * 22.5).toFixed(2));
  const initialParam15 = parseFloat((125 + Math.sin(phase + 10) * 25).toFixed(2));
  const initialGasYield = parseFloat((0.4 + Math.sin(phase + 14) * 0.1).toFixed(2));
  const initialTemp = parseFloat((25 + Math.sin(phase + 11) * 10).toFixed(2));
  const initialPressure = parseFloat((1.5 + Math.sin(phase + 12) * 0.5).toFixed(2));
  const initialPH = parseFloat((7 + Math.sin(phase + 13) * 1).toFixed(2));
  // Initial dynamic data using sinusoidal trends
  const [data, setData] = useState({
    timestamp: new Date().toLocaleString(),
    param1: initialParam1,
    param2: initialParam2,
    param3: initialParam3,
    param4: initialParam4,
    param9: initialParam9,
    param10: initialParam10,
    param11: initialParam11,
    param12: initialParam12,
    param13: initialParam13,
    param14: initialParam14,
    param15: initialParam15
  });
  const [hourlyData, setHourlyData] = useState({
    totalizer: (Math.random() * 1000000).toFixed(0),
    comp_totalizer: (Math.random() * 1000000).toFixed(0)
  });
  // Mock chart data with sinusoidal trends
  const generateTrendData = () => {
    const dataPoints = [];
    for (let i = 23; i >= 0; i--) {
      const time = new Date(Date.now() - i * 3600000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const timePhase = (Date.now() - i * 3600000) / 3600000; // phase in hours
      const production = 75 + Math.sin(timePhase * Math.PI / 12) * 25 + (Math.random() - 0.5) * 2;
      const methane = 60 + Math.sin(timePhase * Math.PI / 6 + Math.PI / 2) * 10 + (Math.random() - 0.5) * 1;
      const feedstock = 100 + Math.sin(timePhase * Math.PI / 24) * 20 + (Math.random() - 0.5) * 2;
      const gasOutput = 65 + Math.sin(timePhase * Math.PI / 12 + Math.PI) * 15 + (Math.random() - 0.5) * 2;
      dataPoints.push({
        time,
        production: parseFloat(production.toFixed(2)),
        methane: parseFloat(methane.toFixed(2)),
        feedstock: parseFloat(feedstock.toFixed(2)),
        gasOutput: parseFloat(gasOutput.toFixed(2))
      });
    }
    return dataPoints;
  };

  const generateMonthlyData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
    const globalPhase = Date.now() / 10000000; // very slow phase for monthly
    const dataPoints = months.map((month, index) => {
      const phase = index + globalPhase;
      const feedstock = 100 + Math.sin(phase) * 20;
      const gasOutput = 50 + Math.sin(phase + 1) * 15;
      return {
        month,
        feedstock: parseFloat(feedstock.toFixed(2)),
        gasOutput: parseFloat(gasOutput.toFixed(2))
      };
    });
    return dataPoints;
  };

  const [gasTrendData, setGasTrendData] = useState(generateTrendData());
  const [radarData, setRadarData] = useState([
    { subject: 'Temperature (°C)', A: initialTemp, fullMark: 40 },
    { subject: 'Pressure (bar)', A: initialPressure, fullMark: 3 },
    { subject: 'pH', A: initialPH, fullMark: 14 },
    { subject: 'CH₄ %', A: initialParam9, fullMark: 100 },
    { subject: 'H₂S ppm', A: initialParam11, fullMark: 100 }
  ]);
  const [feedstockData, setFeedstockData] = useState(generateMonthlyData());
  const [gasYield, setGasYield] = useState(initialGasYield); // m³/kg VS
  // Static data for powered off
  const staticTrendData = Array.from({ length: 24 }, (_, i) => {
    const hour = String(i).padStart(2, '0');
    return {
      time: `${hour}:00`,
      production: 50.00,
      methane: 60.00,
      feedstock: 100.00,
      gasOutput: 45.00
    };
  });

  const staticMonthlyData = [
    { month: 'Jan', feedstock: 90.00, gasOutput: 40.00 },
    { month: 'Feb', feedstock: 95.00, gasOutput: 42.00 },
    { month: 'Mar', feedstock: 100.00, gasOutput: 45.00 },
    { month: 'Apr', feedstock: 105.00, gasOutput: 48.00 },
    { month: 'May', feedstock: 110.00, gasOutput: 50.00 },
    { month: 'Jun', feedstock: 115.00, gasOutput: 52.00 },
    { month: 'Jul', feedstock: 120.00, gasOutput: 55.00 },
    { month: 'Aug', feedstock: 125.00, gasOutput: 58.00 },
    { month: 'Sep', feedstock: 130.00, gasOutput: 60.00 }
  ];

  const staticRadarData = [
    { subject: 'Temperature (°C)', A: 25.00, fullMark: 40 },
    { subject: 'Pressure (bar)', A: 1.50, fullMark: 3 },
    { subject: 'pH', A: 7.00, fullMark: 14 },
    { subject: 'CH₄ %', A: 60.00, fullMark: 100 },
    { subject: 'H₂S ppm', A: 10.00, fullMark: 100 }
  ];
  // Function to generate new sequential data
  const refreshData = () => {
    const timestamp = new Date().toLocaleString();
    const nowTime = Date.now();
    const phase = nowTime / 100000;
    const newParam1 = parseFloat((35 + Math.sin(phase) * 35).toFixed(2));
    const newParam2 = parseFloat((0.5 + Math.sin(phase + 1) * 0.5).toFixed(2));
    const newParam3 = parseFloat((10 + Math.sin(phase + 2) * 10).toFixed(2));
    const newParam4 = parseFloat((250 + Math.sin(phase + 3) * 150).toFixed(2));
    const newParam9 = parseFloat((32.5 + Math.sin(phase + 4) * 32.5).toFixed(2));
    const newParam10 = parseFloat((0.5 + Math.sin(phase + 5) * 0.5).toFixed(2));
    const newParam11 = parseFloat((9 + Math.sin(phase + 6) * 9).toFixed(2));
    const newParam12 = parseFloat((300 + Math.sin(phase + 7) * 150).toFixed(2));
    const newParam13 = parseFloat((25 + Math.sin(phase + 8) * 25).toFixed(2));
    const newParam14 = parseFloat((22.5 + Math.sin(phase + 9) * 22.5).toFixed(2));
    const newParam15 = parseFloat((125 + Math.sin(phase + 10) * 25).toFixed(2));
    const newData = {
      timestamp,
      param1: newParam1,
      param2: newParam2,
      param3: newParam3,
      param4: newParam4,
      param9: newParam9,
      param10: newParam10,
      param11: newParam11,
      param12: newParam12,
      param13: newParam13,
      param14: newParam14,
      param15: newParam15
    };
    setData(newData);
    setHourlyData(prev => ({
      totalizer: (parseFloat(prev.totalizer) + Math.floor(Math.random() * 50 + 20)).toFixed(0),
      comp_totalizer: (parseFloat(prev.comp_totalizer) + Math.floor(Math.random() * 30 + 10)).toFixed(0)
    }));
    setGasTrendData(generateTrendData());
    setFeedstockData(generateMonthlyData());
    setGasYield(parseFloat((0.4 + Math.sin(phase + 14) * 0.1).toFixed(2)));
    setRadarData([
      { subject: 'Temperature (°C)', A: parseFloat((25 + Math.sin(phase + 11) * 10).toFixed(2)), fullMark: 40 },
      { subject: 'Pressure (bar)', A: parseFloat((1.5 + Math.sin(phase + 12) * 0.5).toFixed(2)), fullMark: 3 },
      { subject: 'pH', A: parseFloat((7 + Math.sin(phase + 13) * 1).toFixed(2)), fullMark: 14 },
      { subject: 'CH₄ %', A: newParam9, fullMark: 100 },
      { subject: 'H₂S ppm', A: newParam11, fullMark: 100 }
    ]);
  };
  // Auto-refresh when powered on
  useEffect(() => {
    if (isPowered) {
      const interval = setInterval(refreshData, 60000); // Update every 1 minute
      return () => clearInterval(interval);
    } else {
      // Set to fixed data when powered off
      setData(fixedData);
      setGasTrendData(staticTrendData);
      setFeedstockData(staticMonthlyData);
      setGasYield(0.40);
      setRadarData(staticRadarData);
      setHourlyData({
        totalizer: '500000',
        comp_totalizer: '250000'
      });
    }
  }, [isPowered]);
  // Initial load animation control
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoad(false), 1000);
    return () => clearTimeout(timer);
  }, []);
  // Toggle function for power
  const togglePower = () => {
    setIsPowered(prev => !prev);
  };
  // Icons mapping
  const icons = {
    fire: <FlameIcon className="w-8 h-8 text-orange-500" />,
    gas: <GaugeIcon className="w-8 h-8 text-blue-500" />,
    methane: <CloudIcon className="w-8 h-8 text-green-500" />,
    co2: <DropletIcon className="w-8 h-8 text-red-500" />,
    h2s: <WindIcon className="w-8 h-8 text-purple-500" />,
    o2: <OctagonIcon className="w-8 h-8 text-teal-500" />
  };
 
  // Gauge color logic
  const getGaugeColor = () => {
    if (gasYield > 0.4) return '#10B981'; // green
    if (gasYield > 0.3) return '#F59E0B'; // yellow
    return '#EF4444'; // red
  };
  // ApexCharts Options for Gas Production Trend
  const gasProductionOptions = {
    chart: {
      type: 'line',
      height: 200,
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    grid: {
      show: true,
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      position: 'back',
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      row: { colors: undefined, opacity: 0.5 },
      column: { colors: undefined, opacity: 0.5 }
    },
    xaxis: {
      categories: gasTrendData.map(d => d.time),
      labels: { style: { fontSize: '10px' } }
    },
    yaxis: [
      {
        title: { text: 'Production (m³/h)', style: { fontSize: '12px' } },
        labels: {
          style: { fontSize: '10px' },
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      },
      {
        opposite: true,
        title: { text: 'Methane %', style: { fontSize: '12px' } },
        labels: {
          style: { fontSize: '10px' },
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      }
    ],
    legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function(val) {
          return val.toFixed(2);
        }
      }
    },
    colors: ['#3B82F6', '#10B981']
  };
  const gasProductionSeries = [
    { name: 'Production (m³/h)', data: gasTrendData.map(d => d.production) },
    { name: 'Methane %', data: gasTrendData.map(d => d.methane) }
  ];
  // ApexCharts Options for Digester Health Radar
  const radarOptions = {
    chart: {
      type: 'radar',
      height: 200,
      toolbar: { show: false },
      dropShadow: { enabled: true, blurX: 1, blurY: 1, opacity: 0.2 }
    },
    stroke: { width: 2 },
    fill: { opacity: 0.6 },
    grid: { show: true },
    xaxis: {
      categories: radarData.map(d => d.subject),
      labels: { style: { fontSize: '10px' } }
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        style: { fontSize: '10px' },
        formatter: function(val) {
          return val.toFixed(2);
        }
      }
    },
    legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
    plotOptions: {
      radar: {
        polygons: { strokeColors: '#e8e8e8', strokeWidth: 1, connectorColors: '#e8e8e8' }
      }
    },
    colors: ['#3B82F6'],
    markers: { size: 4, colors: ['#3B82F6'], strokeColor: '#fff', strokeWidth: 2 },
    tooltip: {
      y: {
        formatter: function(val) {
          return val.toFixed(2);
        }
      }
    }
  };
  const radarSeries = [{ name: 'Health', data: radarData.map(d => d.A) }];
  // ApexCharts Options for Feedstock vs Gas Output
  const feedstockOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    stroke: {
      curve: 'straight',
      width: 2
    },
    markers: {
      size: 5,
      colors: ['#3B82F6', '#10B981']
    },
    grid: {
      show: true,
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5
      }
    },
    title: {
      text: 'Product Trends by Month',
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#374151'
      }
    },
    xaxis: {
      categories: feedstockData.map(d => d.month),
      labels: { style: { fontSize: '10px' } }
    },
    yaxis: [
      {
        title: { text: 'Feedstock (m³/h)', style: { fontSize: '12px' } },
        labels: {
          style: { fontSize: '10px' },
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      },
      {
        opposite: true,
        title: { text: 'Gas Output (m³/h)', style: { fontSize: '12px' } },
        labels: {
          style: { fontSize: '10px' },
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      }
    ],
    legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function(val) {
          return val.toFixed(2);
        }
      }
    },
    colors: ['#3B82F6', '#10B981'],
    dataLabels: { enabled: false }
  };
  const feedstockSeries = [
    { name: 'Feedstock (m³/h)', data: feedstockData.map(d => d.feedstock) },
    { name: 'Gas Output (m³/h)', data: feedstockData.map(d => d.gasOutput) }
  ];
  // ApexCharts Options for Cumulative Production (Bar Chart)
  const cumulativeOptions = {
    chart: {
      type: 'bar',
      height: 320,
      toolbar: { show: false },
      background: 'transparent'
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 12,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function(val) {
        return (val / 1000).toFixed(0) + 'k';
      },
      offsetY: -25,
      style: {
        fontSize: '14px',
        colors: ['#374151'],
        fontWeight: 700
      }
    },
    xaxis: {
      categories: ['Total Biogas', 'Compressed Gas'],
      labels: {
        style: {
          fontSize: '12px',
          colors: '#6B7280',
          fontWeight: 600
        }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        show: false
      }
    },
    colors: ['#3B82F6', '#10B981'],
    grid: {
      show: false
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: function(val) {
          return val.toLocaleString() + ' m³';
        }
      }
    }
  };
  const cumulativeSeries = [
    {
      name: 'Volume (m³)',
      data: [parseFloat(hourlyData.totalizer), parseFloat(hourlyData.comp_totalizer)]
    }
  ];
  // ApexCharts Options for Efficiency KPI Gauge
  const gaugeOptions = {
    chart: {
      type: 'radialBar',
      height: 200,
      toolbar: { show: false }
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        track: { background: '#e7e7e7', strokeWidth: '10%' },
        dataLabels: {
          name: { show: false },
          value: {
            offsetY: 10,
            fontSize: '16px',
            color: '#373d3f',
            formatter: function (val) {
              return (val / 1000).toFixed(2) + ' m³/kg VS';
            }
          }
        },
        hollow: {
          margin: 15,
          size: '70%',
          background: 'transparent'
        },
        colors: [getGaugeColor()]
      }
    },
    labels: ['Gas Yield'],
    fill: { type: 'gradient', gradient: { shade: 'light', type: 'horizontal', shadeIntensity: 0.1, gradientToColors: ['#fff'] } },
    stroke: { lineCap: 'round' }
  };
  const gaugeSeries = [gasYield * 1000]; // Scaled for display
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
      <UserHeader />
      <div className="flex items-center space-x-4 w-full mt-3 px-5">
        <button
          onClick={() => setCurrentView('dashboard')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${currentView === 'dashboard'
              ? 'bg-teal-700 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </button>
        <button
          onClick={() => setCurrentView('report')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${currentView === 'report'
              ? 'bg-teal-700 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
          <FileText size={20} />
          <span>Reports</span>
        </button>
      </div>
     
      {/* Conditional Rendering of Dashboard or Reports */}
      <AnimatePresence mode="wait">
        <motion.main
          key={currentView}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: initialLoad ? 0.3 : 0 }}
          className="flex-grow p-6 overflow-x-hidden"
        >
          {currentView === 'dashboard' ? (
            <div className="bg-white shadow-md rounded-lg p-6 max-w-full overflow-x-hidden">
              {/* Existing dashboard content */}
              <div className="text-2xl font-bold mb-6 text-gray-800">
                Device Dashboard: HW-1
              </div>
              {/* SVG content */}
              <div className="relative mb-6 w-full max-w-full overflow-hidden">
                <div className="relative w-full max-w-full">
                  {/* Base Product SVG */}
                  <img className="w-full h-auto max-w-full" src="/Assets/bioproduct.svg" alt="Product" />
                  {/* Overlay SVG (data visualization layer) */}
                  <svg
                    className="absolute top-0 left-0 w-full h-full pointer-events-none max-w-full"
                    viewBox="0 0 1000 1000"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    {/* Power indicator circle */}
                    <circle cx="-140" cy="530" r="10" fill={isPowered ? "green" : "red"} />
                    {/* Floating gray ellipses animation - only when powered on */}
                    {isPowered && (
                      <g fill="gray" transform="matrix(1, 0, 0, 1, 910.660461, 255.48898)">
                        <ellipse cx="333" cy="105" rx="10" ry="5">
                          <animateTransform
                            attributeName="transform"
                            type="translate"
                            values="0,0; 0,-30"
                            dur="3s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.5;0.3;0"
                            dur="3s"
                            repeatCount="indefinite"
                          />
                        </ellipse>
                        <ellipse cx="338" cy="95" rx="12" ry="6">
                          <animateTransform
                            attributeName="transform"
                            type="translate"
                            values="0,0; 0,-35"
                            dur="4s"
                            repeatCount="indefinite"
                            begin="0.5s"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.4;0.2;0"
                            dur="4s"
                            repeatCount="indefinite"
                            begin="0.5s"
                          />
                        </ellipse>
                        <ellipse cx="328" cy="100" rx="8" ry="4">
                          <animateTransform
                            attributeName="transform"
                            type="translate"
                            values="0,0; 0,-40"
                            dur="3.5s"
                            repeatCount="indefinite"
                            begin="1s"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.3;0.1;0"
                            dur="3.5s"
                            repeatCount="indefinite"
                            begin="1s"
                          />
                        </ellipse>
                      </g>
                    )}
                    {/* Anaerobic Bio Digestor - Dynamic Texts */}
                    <text
                      x="179.694"
                      y="259.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      O₂ : {data.param10.toFixed(2)} % v/v
                    </text>
                    <text
                      x="179.694"
                      y="319.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      CO₂ : {data.param12.toFixed(2)} ppm
                    </text>
                    <text
                      x="109.694"
                      y="289.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      CH₄ : {data.param9.toFixed(2)} % v/v
                    </text>
                    <text
                      x="259.694"
                      y="289.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      H₂S : {data.param11.toFixed(2)} ppm
                    </text>
                    {/* Translating arrow animation - only when powered on */}
                    {isPowered && (
                      <>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8, -541.853261440344, 493.471361575337)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8, -181.853261440344, 700.471361575337)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(0, -1.8, 1.8, 0, -71.853261440344, 603.471361575337)"
                          style={{ transformOrigin: "12px 12px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8, -5.853261440344, 500.471361575337)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(0, 1.8, -1.8, 0, 235.853261440344, 783.471361575337)"
                          style={{ transformOrigin: "12px 12px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8, 445.853261440344, 890.471361575337)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                          <g
                          transform="matrix(1.8, 0, 0, 1.8,1470, 440)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(0, -1.8, 1.8, 0, 645.853261440344, 790.471361575337)"
                          style={{ transformOrigin: "12px 12px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(0, 1.8, -1.8, 0, 815.853261440344, 270.471361575337)"
                          style={{ transformOrigin: "12px 12px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8, 655.853261440344,100.471361575337)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8,900, 440)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(1.8, 0, 0, 1.8,900, 440)"
                          style={{ transformOrigin: "22px 22px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                        <g
                          transform="matrix(0, -1.8, 1.8, 0, 485.853261440344, 290.471361575337)"
                          style={{ transformOrigin: "12px 12px" }}
                        >
                          <path
                            d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
                            fill="rgb(213, 213, 213)"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="translate"
                              from="-5 0"
                              to="5 0"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.5;1;0.5"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                      </>
                    )}
                    {/* Feed Stock Label - Dynamic */}
                    <g transform="matrix(1.8, 0, 0, 1.8, -730.853, 220.471)">
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="6"
                          markerHeight="6"
                          refX="5"
                          refY="3"
                          orient="auto"
                          fill="gray"
                        >
                          <path d="M0,0 L6,3 L0,6 Z" />
                        </marker>
                      </defs>
                      {/* angled gray arrow */}
                      <path
                        d="M50 150 L80 100 L120 100"
                        stroke="gray"
                        strokeWidth="1"
                        fill="none"
                        markerEnd="url(#arrowhead)"
                      />
                      {/* two-line label */}
                      <text
                        x="125"
                        y="104"
                        textAnchor="start"
                        style={{
                          fontFamily: "Arial, sans-serif",
                          fontSize: "8px",
                          fill: "gray",
                        }}
                      >
                        <tspan x="125" dy="0">Feed Stock</tspan>
                        <tspan x="125" dy="10">{data.param15.toFixed(2)} m³/h</tspan>
                      </text>
                    </g>
                    {/* Raw Bio Gas - Dynamic */}
                    <g transform="matrix(0, -1.8, 1.8, 0, 271.853261440344, 603.471361575337)">
                      <text
                        x="125"
                        y="104"
                        style={{
                          fontFamily: "Arial, sans-serif",
                          fontSize: "10px",
                          fill: "gray",
                        }}
                      >
                        Raw Bio Gas - {data.param14.toFixed(2)} m³/h
                      </text>
                    </g>
                    {/* Comp Bio Gas - Dynamic */}
                    <g transform="matrix(1.8, 0, 0, 1.8, 1300, 180.471)">
                      <defs>
                        <marker
                          id="arrowhead2"
                          markerWidth="6"
                          markerHeight="6"
                          refX="5"
                          refY="3"
                          orient="auto"
                          fill="gray"
                        >
                          <path d="M0,0 L6,3 L0,6 Z" />
                        </marker>
                      </defs>
                      {/* compact angled gray arrow */}
                      <path
                        d="M50 140 L70 120 L90 120"
                        stroke="gray"
                        strokeWidth="1"
                        fill="none"
                        markerEnd="url(#arrowhead2)"
                      />
                      {/* two-line label */}
                      <text
                        x="95"
                        y="124"
                        textAnchor="start"
                        style={{
                          fontFamily: "Arial, sans-serif",
                          fontSize: "8px",
                          fill: "gray",
                        }}
                      >
                        <tspan x="95" dy="0">Comp Bio Gas</tspan>
                        <tspan x="95" dy="10">{data.param13.toFixed(2)} m³/h</tspan>
                      </text>
                    </g>
                    {/* Bio Gas Scrubber Unit - Dynamic Texts */}
                    <text
                      x="1059.694"
                      y="425.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      O₂ : {data.param2.toFixed(2)} % v/v
                    </text>
                    <text
                      x="1199.694"
                      y="425.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      CO₂ : {data.param4.toFixed(2)} ppm
                    </text>
                    <text
                      x="1059.694"
                      y="465.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      CH₄ : {data.param1.toFixed(2)} % v/v
                    </text>
                    <text
                      x="1199.694"
                      y="465.903"
                      transform="matrix(1, 0, 0, 1, 0, 2.842170943040401e-14)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        whiteSpace: "pre"
                      }}
                      id="aco21"
                    >
                      H₂S : {data.param3.toFixed(2)} ppm
                    </text>
                    {/* Inlet/Outlet H2S - Dynamic */}
                    <text
                      x="600.694"
                      y="605.903"
                      transform="matrix(1, 0, 0, 1, 0, 0)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        fill: "black",
                      }}
                      id="aco21"
                    >
                      <tspan x="600.694" dy="0">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Inlet</tspan>
                      <tspan x="600.694" dy="18">H₂S : {data.param11.toFixed(2)} ppm</tspan>
                    </text>
                    <text
                      x="600.694"
                      y="255.903"
                      transform="matrix(1, 0, 0, 1, 0, 0)"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "15px",
                        fill: "black",
                      }}
                      id="aco21"
                    >
                      <tspan x="600.694" dy="0">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Outlet</tspan>
                      <tspan x="600.694" dy="18">H₂S : {data.param3.toFixed(2)} ppm</tspan>
                    </text>
                  </svg>
                </div>
               
                {/* Power toggle button */}
                <button
                  onClick={togglePower}
                  className={`absolute bottom-4 right-4 rounded-full p-3 shadow-lg ${isPowered ? 'bg-green-500' : 'bg-red-500'} hover:scale-110 transition-transform`}
                >
                  <Power className="w-6 h-6 text-white" />
                </button>
              </div>
              {/* Refresh button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={refreshData}
                  disabled={!isPowered}
                  className={`px-4 py-2 rounded transition ${isPowered ? 'bg-teal-700 text-white hover:bg-teal-600' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}
                >
                  Refresh Data
                </button>
              </div>
              {/* Metric Cards */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Anaerobic Digestor Unit */}
                <motion.div
                  className="bg-white shadow-md rounded-lg p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: initialLoad ? 0.5 : 0 }}
                >
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">
                    Anaerobic Digestor Unit
                  </h2>
                  <div className="text-sm text-gray-500 mb-4">
                    Last updated: {data.timestamp}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Raw Bio-Gas */}
                    <div className="bg-gray-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.fire}
                      <div className="ml-4">
                        <div className="text-lg font-semibold">
                          {data.param14.toFixed(2)} m³/h
                        </div>
                        <div className="text-xs text-gray-500">Raw Bio-Gas</div>
                      </div>
                    </div>
                    {/* Total Bio-Gas */}
                    <div className="bg-gray-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.gas}
                      <div className="ml-4">
                        <div className="text-lg font-semibold">
                          {hourlyData.totalizer}
                        </div>
                        <div className="text-xs text-gray-500">Raw Bio-Gas Total</div>
                      </div>
                    </div>
                    {/* Methane */}
                    <div className="bg-gray-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.methane}
                      <div className="ml-4">
                        <div className="text-lg font-semibold">
                          {data.param9.toFixed(2)} %v/v
                        </div>
                        <div className="text-xs text-gray-500">Methane</div>
                      </div>
                    </div>
                    {/* Carbon Dioxide */}
                    <div className="bg-gray-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.co2}
                      <div className="ml-4">
                        <div className="text-lg font-semibold">
                          {data.param12.toFixed(2)} ppm
                        </div>
                        <div className="text-xs text-gray-500">Carbon Di-Oxide</div>
                      </div>
                    </div>
                    {/* Hydrogen Sulfide */}
                    <div className="bg-gray-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.h2s}
                      <div className="ml-4">
                        <div className="text-lg font-semibold">
                          {data.param11.toFixed(2)} ppm
                        </div>
                        <div className="text-xs text-gray-500">Hydrogen Sulfide</div>
                      </div>
                    </div>
                    {/* Oxygen */}
                    <div className="bg-gray-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.o2}
                      <div className="ml-4">
                        <div className="text-lg font-semibold">
                          {data.param10.toFixed(2)} %v/v
                        </div>
                        <div className="text-xs text-gray-500">O2</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
                {/* Bio-Gas Upgradation Unit */}
                <motion.div
                  className="bg-white shadow-md rounded-lg p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: initialLoad ? 0.5 : 0, delay: 0.2 }}
                >
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">
                    Bio-Gas Upgradation Unit
                  </h2>
                  <div className="text-sm text-gray-500 mb-4">
                    Last updated: {data.timestamp}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Raw Bio-Gas */}
                    <div className="bg-red-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.fire}
                      <div className="ml-4">
                        <div className="text-lg font-semibold text-red-700">
                          {data.param13.toFixed(2)} m³/h
                        </div>
                        <div className="text-xs text-red-500">Raw Bio-Gas</div>
                      </div>
                    </div>
                    {/* Compressed Bio-Gas */}
                    <div className="bg-red-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.gas}
                      <div className="ml-4">
                        <div className="text-lg font-semibold text-red-700">
                          {hourlyData.comp_totalizer}
                        </div>
                        <div className="text-xs text-red-500">Comp Bio-Gas</div>
                      </div>
                    </div>
                    {/* Methane */}
                    <div className="bg-red-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.methane}
                      <div className="ml-4">
                        <div className="text-lg font-semibold text-red-700">
                          {data.param1.toFixed(2)} %v/v
                        </div>
                        <div className="text-xs text-red-500">Methane</div>
                      </div>
                    </div>
                    {/* Carbon Dioxide */}
                    <div className="bg-red-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.co2}
                      <div className="ml-4">
                        <div className="text-lg font-semibold text-red-700">
                          {data.param4.toFixed(2)} ppm
                        </div>
                        <div className="text-xs text-red-500">Carbon Di-Oxide</div>
                      </div>
                    </div>
                    {/* Hydrogen Sulfide */}
                    <div className="bg-red-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.h2s}
                      <div className="ml-4">
                        <div className="text-lg font-semibold text-red-700">
                          {data.param3.toFixed(2)} ppm
                        </div>
                        <div className="text-xs text-red-500">Hydrogen Sulfide</div>
                      </div>
                    </div>
                    {/* Oxygen */}
                    <div className="bg-red-50 p-4 rounded-lg flex items-center hover:scale-105 transition-transform">
                      {icons.o2}
                      <div className="ml-4">
                        <div className="text-lg font-semibold text-red-700">
                          {data.param2.toFixed(2)} %v/v
                        </div>
                        <div className="text-xs text-red-500">O2</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              {/* Analytics Section Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Performance Analytics</h2>
                <p className="text-sm text-gray-500">Real-time monitoring and historical trends</p>
              </div>
              {/* Modern Charts Section - Clean Minimal Design */}
              <div className="space-y-6 w-full max-w-full overflow-x-hidden">
                {/* Top Row - Gas Production Trend + Cumulative Production */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Gas Production Trend */}
                  <div className="lg:col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 max-w-full">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">Gas Production Trend</h3>
                        <p className="text-xs text-gray-600 mt-1">Last 24 hours performance</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                          <span className="text-xs text-gray-600">Production</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                          <span className="text-xs text-gray-600">Methane</span>
                        </div>
                      </div>
                    </div>
                    <div className="w-full h-[320px] max-w-full">
                      <Chart
                        options={{
                          chart: {
                            type: 'area',
                            height: 320,
                            toolbar: { show: false },
                            zoom: { enabled: false },
                            background: 'transparent'
                          },
                          stroke: {
                            curve: 'smooth',
                            width: 3
                          },
                          fill: {
                            type: 'gradient',
                            gradient: {
                              shadeIntensity: 1,
                              opacityFrom: 0.6,
                              opacityTo: 0.1,
                              stops: [0, 90, 100]
                            }
                          },
                          grid: {
                            show: false
                          },
                          xaxis: {
                            categories: gasTrendData.map(d => d.time),
                            labels: {
                              style: {
                                fontSize: '11px',
                                colors: '#6B7280'
                              }
                            },
                            axisBorder: { show: false },
                            axisTicks: { show: false }
                          },
                          yaxis: [
                            {
                              title: {
                                text: 'Production (m³/h)',
                                style: {
                                  fontSize: '12px',
                                  color: '#6B7280',
                                  fontWeight: 600
                                }
                              },
                              labels: {
                                style: {
                                  fontSize: '11px',
                                  colors: '#6B7280'
                                },
                                formatter: function(val) {
                                  return val.toFixed(2);
                                }
                              }
                            },
                            {
                              opposite: true,
                              title: {
                                text: 'Methane %',
                                style: {
                                  fontSize: '12px',
                                  color: '#6B7280',
                                  fontWeight: 600
                                }
                              },
                              labels: {
                                style: {
                                  fontSize: '11px',
                                  colors: '#6B7280'
                                },
                                formatter: function(val) {
                                  return val.toFixed(2);
                                }
                              }
                            }
                          ],
                          legend: { show: false },
                          tooltip: {
                            shared: true,
                            intersect: false,
                            theme: 'light',
                            style: {
                              fontSize: '12px'
                            },
                            y: {
                              formatter: function(val) {
                                return val.toFixed(2);
                              }
                            }
                          },
                          colors: ['#3B82F6', '#10B981'],
                          dataLabels: { enabled: false }
                        }}
                        series={gasProductionSeries}
                        type="area"
                        height={320}
                      />
                    </div>
                  </div>
                  {/* Cumulative Production */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 max-w-full">
                    <div className="mb-6">
                      <h3 className="font-bold text-gray-900 text-lg">Cumulative Production</h3>
                      <p className="text-xs text-gray-600 mt-1">Total volume metrics</p>
                    </div>
                    <div className="w-full h-[320px] flex flex-col justify-center max-w-full">
                      <Chart
                        options={cumulativeOptions}
                        series={cumulativeSeries}
                        type="bar"
                        height={320}
                      />
                    </div>
                  </div>
                </div>
                {/* Bottom Row - Feedstock vs Gas Output Analysis (Full Width) */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 max-w-full">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">Feedstock vs Gas Output Analysis</h3>
                      <p className="text-xs text-gray-600 mt-1">24-hour correlation and efficiency trend</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                        <span className="text-xs text-gray-600">Feedstock</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                        <span className="text-xs text-gray-600">Gas Output</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-[350px] max-w-full">
                    <Chart
                      options={feedstockOptions}
                      series={feedstockSeries}
                      type="line"
                      height={350}
                    />
                  </div>
                </div>
              </div>
      
            </div>
          ) : (
            <UserHistory />
          )}
        </motion.main>
      </AnimatePresence>
     
      <footer className="bg-white border-t border-gray-200 mt-8 py-8">
        <div className="container mx-auto px-6">
          <div className="mt-8 pt-6 border-t border-gray-100 text-center text-sm text-gray-500">
            © 2025 OliveIoT. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
export default UserDashboard;